import clsx from 'clsx';
import { CSSProperties, ReactElement, useCallback, useMemo, useState } from 'react';

import { getIsRTL } from '@vestico/i18n';

import { CustomCSSProperties } from '../model';
import { Button } from './Button';
import style from './popover.module.css';

export enum Vertical {
  top,
  bottom,
}

export enum Horizontal {
  left,
  center,
  right,
}

export interface PopoverPosition {
  horizontal: Horizontal;
  vertical: Vertical;
}

export interface PopoverProps {
  activator: string | ReactElement;
  position: PopoverPosition;
  backgroundColor: string;
  children: ReactElement | ReactElement[];
  showArrow?: boolean;
  contentWidth: string;
  minWidth?: string;
  didOpen?: () => void;
  buttonColor?: string;
}

export const Popover = (props: PopoverProps) => {
  const { activator, position, backgroundColor, children, showArrow, contentWidth, didOpen, buttonColor, minWidth } = props;
  const { horizontal, vertical } = position;

  const [open, setOpen] = useState(false);
  const isRTL = getIsRTL();

  const updateOpen = useCallback(() => {
    const newOpen = !open;
    setOpen(newOpen);
    if (newOpen && didOpen) {
      didOpen();
    }
  }, [didOpen, open]);
  const onTrackerClick = useCallback(() => setOpen(false), []);

  const cssStyle = useMemo(() => {
    const cssprops: CustomCSSProperties = { '--color-popover-bg': backgroundColor, width: contentWidth };

    if (minWidth) {
      cssprops.minWidth = minWidth;
    }

    return cssprops as CSSProperties;
  }, [backgroundColor, contentWidth, minWidth]);

  return (
    <div className={clsx(style.root, { [style.open]: open })}>
      <div className={clsx(style.tracker)} onClick={onTrackerClick} aria-hidden="true" />
      <Button content={activator} onClick={updateOpen} plain size="small" color={buttonColor} />
      <div
        className={clsx(style.content, {
          [style.top]: vertical === Vertical.top,
          [style.bottom]: vertical === Vertical.bottom,
          [style.centerHorizontal]: horizontal === Horizontal.center,
          [style.left]: horizontal === Horizontal.left,
          [style.right]: horizontal === Horizontal.right,
          [style.rtl]: isRTL,
          [style.arrow]: !!showArrow,
        })}
        style={cssStyle}
      >
        {children}
      </div>
    </div>
  );
};
